// AGENT-RECORD (docs/direction/2026-10-28-managing-agents-gaps.md §2.1, §3 #1, decision 2): a
// digital employee's track record, the first tab of its page. Fed only from real work: the
// `output.*`, `task.completed` and `intervention.recorded` activity items consumer `agent_record.feed`
// writes from CompanyOS's own approvals, completions, take-overs and cancels (plus any the DE's
// reporter ingests, e.g. supervisor reactions), its `outcome.recorded` items, the client-visible
// cost ledger and approved supervision time of its seat while it held it. Incidents are the
// money incidents that exist today (`budget_incident`); a quality incident object is a later
// build (§2.4), so the record says it is not tracked yet. No ROI figure before four weeks with
// outcomes (`roi.state = locked`).
import { z } from "zod";
import { MetricDirection, MetricUnit } from "../enums";
import { IsoDate, Timestamp, Uuid, WeekKey } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { AgentSpendBudget } from "./finance-views";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** Weeks with outcomes before the record shows an ROI basis (decision 2: "four weeks"). */
export const RECORD_ROI_WEEKS = 4;
/** The trend's default and largest window, in company weeks (the current week included). */
export const RECORD_DEFAULT_WEEKS = 8;
export const RECORD_MAX_WEEKS = 26;

/** One evidence line kind: what happened to (or around) the DE's work. */
export const RecordSignal = z
  .enum([
    "approved_as_is",
    "edited",
    "rejected",
    "completed",
    // A work outcome (the per-outcome figures' denominator); its detail is what it restates
    // (`task.completed`, `review.approved`, `review.edited`).
    "work_outcome",
    "reported_outcome",
    "intervention",
    "incident",
    // DE-KPIS-LIVE: an ended top-level run; its detail is the outcome (`done`, `failed`, `stopped`).
    "run",
    // DECISION-LEDGER: the decision trail. `decided`: the policy let the agent act in its run
    // (detail: the action class); `escalated`: it asked a person (detail: `approval`, `premise`,
    // `agent_escalation`); `reversed`: its work was undone or thrown out as wrong (detail:
    // `undo`, `send_back`; `by_*` is who did it, `reason` why when they said).
    "decided",
    "escalated",
    "reversed",
  ])
  .meta({ id: "RecordSignal" });

/** How a person stepped into the DE's work (`intervention.recorded` `attrs.kind`). */
export const InterventionKind = z.enum(["take_over", "cancel"]).meta({ id: "InterventionKind" });

const count = z.int().min(0);
const rate = z.number().min(0).max(1).nullable();

/** The record's numbers for one span (a week, or the whole window). */
export const RecordFigures = z
  .object({
    approved_as_is: count,
    edited: count,
    rejected: count,
    /** Tasks the DE completed itself (a run or its key moved them to done). */
    completed: count,
    /** Business outcomes the DE reported (`outcome.recorded`): shown, not in `outcomes`. */
    reported_outcomes: count,
    /**
     * Work outcomes: approved_as_is + edited + completed (the catalog's `de.work_outcomes`; one
     * piece of work is not counted again as the business result it led to).
     */
    outcomes: count,
    interventions: count,
    incidents: count,
    /** Client-visible cost on the DE's seat while it held it, reporting currency, minor units. */
    cost_minor: z.int(),
    /** Approved supervision time on the DE's seat while it held it, minutes. */
    supervisor_minutes: count,
    /** Shares of the reviewed outputs (approved as-is + edited + rejected); null with none. */
    approved_as_is_rate: rate,
    edited_rate: rate,
    rejected_rate: rate,
    /** cost_minor / outcomes, rounded; null with no outcomes. */
    cost_per_outcome_minor: z.int().nullable(),
    /** supervisor_minutes / outcomes, one decimal; null with no outcomes. */
    supervisor_minutes_per_outcome: z.number().min(0).nullable(),
    /**
     * DE-KPIS-LIVE: the DE's ended top-level runs (`run.*` items of the record's feeder): done
     * (completed, its output not marked failing verification), failed or blocked, stopped early
     * (cancelled, taken over, paused or retired, or left unreviewed). A run lost to infrastructure
     * and retried is counted once, by its successor.
     */
    runs: count,
    runs_done: count,
    runs_failed: count,
    runs_stopped: count,
    /** TASK-BUDGETS: of `runs_stopped`, those a hard limit stopped (steps, cost, time). */
    runs_budget_stopped: count,
    /** runs_done / runs; null with no runs. */
    run_success_rate: rate,
    /** cost_minor / runs, rounded; null with no runs. */
    cost_per_run_minor: z.int().nullable(),
    /** Memory CompanyOS handed the DE's runs (brief, resume, procedure) and saves they asked for. */
    memory_reads: count,
    memory_saves: count,
    /**
     * DECISION-LEDGER (roadmap Phase 1 item 1): actions the agent policy let the DE take inside
     * its run; times it asked a person (approvals it requested, premises and handoffs it
     * escalated); and its work undone or thrown out as wrong (an undo of its write, a send-back
     * marked wrong). An edit is not a reversal.
     */
    decided: count,
    escalated: count,
    reversed: count,
  })
  .meta({ id: "RecordFigures" });

/**
 * DE-KPIS-LIVE: one helper (AG) the DE leads or uses, with what its model calls cost in the
 * window (the ledger rows tagged `cos-ag:<slug>` booked to the DE). Helpers report no runs to
 * CompanyOS, so they have none here.
 */
export const RecordHelper = z
  .object({
    id: Uuid,
    name: z.string(),
    slug: z.string(),
    status: z.enum(["active", "paused", "retired", "not_deployed"]),
    /** `lead`: it reports to this DE; `uses`: this DE uses another's helper. */
    relation: z.enum(["lead", "uses"]),
    cost_minor: z.int(),
    /** Ledger rows (model calls) in the window. */
    calls: count,
    /**
     * Its jobs in the window: its ended attempts at the DE's helper cards (per attempt, by the
     * profile that ran it; a card with no attempts reported, once for its assignee), done,
     * blocked (blocked, failed or crashed), and done out of all (null with none).
     */
    jobs: count,
    jobs_done: count,
    jobs_blocked: count,
    job_success_rate: z.number().min(0).max(1).nullable(),
    last_activity: z.string().nullable(),
    last_activity_at: Timestamp.nullable(),
  })
  .meta({ id: "RecordHelper" });

/**
 * DE-KPIS-LIVE: where the memory figures come from. `companyos`: the memory calls CompanyOS made
 * for the DE's runs; `null`: memory is not on for the DE (no run was ever handed memory).
 * `brief_use` is Recall's share of briefs and recalls a run cited (its memory-usage API); null
 * until that endpoint answers.
 */
export const RecordMemory = z
  .object({
    source: z.enum(["companyos", "recall"]).nullable(),
    brief_use: rate,
  })
  .meta({ id: "RecordMemory" });

/**
 * DE-KPIS-LIVE: one outcome KPI of the DE's role (`ROLE_OUTCOME_KPIS`) that is on its seat, over
 * the window. `fed`: `companyos` when CompanyOS feeds it from what it sees happen (a count is 0
 * when nothing happened), `reported` when the DE reports it (`tracked` false and `value` null
 * until it has: "not tracked yet"). `target`: the seat's weekly target now, else the company's.
 */
export const RecordOutcome = z
  .object({
    key: z.string(),
    label: z.string(),
    unit: MetricUnit,
    direction: MetricDirection,
    fed: z.enum(["companyos", "reported"]),
    tracked: z.boolean(),
    value: z.number().nullable(),
    target: z.number().nullable(),
  })
  .meta({ id: "RecordOutcome" });

export const RecordWeek = RecordFigures.extend({
  week: WeekKey,
  starts_on: IsoDate,
  ends_on: IsoDate,
}).meta({ id: "RecordWeek" });

export const RecordRoi = z
  .discriminatedUnion("state", [
    z.object({
      state: z.literal("locked"),
      weeks_with_outcomes: count,
      weeks_needed: z.int().min(1),
    }),
    z.object({
      state: z.literal("ready"),
      weeks_with_outcomes: count,
      weeks_needed: z.int().min(1),
      /** The last `weeks_needed` weeks: the basis an ROI is read from. */
      basis: RecordFigures,
    }),
  ])
  .meta({ id: "RecordRoi" });

export const DeRecord = z
  .object({
    de_membership_id: Uuid,
    currency: z.string().length(3),
    /** Oldest first; the last is the current (partial) week. */
    weeks: z.array(RecordWeek),
    /** The whole window. */
    total: RecordFigures,
    interventions_by_kind: z.record(InterventionKind, count),
    /** Which incident kinds exist to count: money today; quality incidents are not built yet. */
    incidents_tracked: z.array(z.enum(["budget"])),
    roi: RecordRoi,
    /** False when the DE has no record at all yet (no record item ever, nothing in the window). */
    has_data: z.boolean(),
    /** False when the window shown has nothing (the DE may have a record in older weeks). */
    window_has_data: z.boolean(),
    /**
     * DE-KPIS-LIVE: spend vs the DE's budget for the budget's period (the ledger); null when it has
     * none or the reader may not read it (`digitalEmployees.budget`'s rule: a manager, or a person
     * above the DE's seat), which `budget_readable` tells apart.
     */
    budget: AgentSpendBudget.nullable(),
    budget_readable: z.boolean(),
    /** DE-KPIS-LIVE: the helpers (AG) it leads, then those it uses, by name. */
    helpers: z.array(RecordHelper),
    memory: RecordMemory,
    /** DE-KPIS-LIVE: the role its outcome KPIs come from (`outcomeRoleOf`) and those KPIs. */
    outcome_role: z.string(),
    outcomes: z.array(RecordOutcome),
  })
  .meta({ id: "DeRecord" });

export const DeRecordQuery = z.object({
  weeks: z.coerce.number().int().min(1).max(RECORD_MAX_WEEKS).optional(),
});

export const RecordEvidenceItem = z
  .object({
    id: z.string().describe("The evidence's own id (the event, or the incident)"),
    signal: RecordSignal,
    at: Timestamp,
    /** What it is about, to open: an approval, a task, a run, a budget incident, or nothing. */
    subject: z.object({ kind: z.string(), id: z.string() }).nullable(),
    run_id: Uuid.nullable(),
    /** The approval kind, the intervention kind, the incident kind, or the outcome type. */
    detail: z.string().nullable(),
    /** The person who decided or stepped in, when there is one. */
    by_membership_id: Uuid.nullable(),
    by_name: z.string().nullable(),
    /** DECISION-LEDGER: why a reversal was made, when the person who made it said. */
    reason: z.string().nullable(),
  })
  .meta({ id: "RecordEvidenceItem" });

export const DeRecordEvidenceQuery = z.object({
  week: WeekKey,
  signal: RecordSignal.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  cursor: z.string().max(512).optional(),
});

/**
 * HELPER-KPIS (design 2026-11-02 §4): `GET digital-employees/{id}/helpers/kpis?period=` — the
 * weekly numbers of the helpers the DE leads or uses, read from `observation` rows with
 * `subject_kind: agent` (platform-derived from attributed board events, or self-reported), and
 * the DE's own board metrics. `period` is a week key (default: the current week).
 */
export const HelperKpisQuery = z.object({ period: WeekKey.optional() });

const HelperKpiValue = z.object({
  key: z.string(),
  name: z.string(),
  unit: MetricUnit,
  direction: MetricDirection,
  /** `event`: derived from board events; `manual`: self-reported by the DE for the helper. */
  source: z.enum(["event", "manual"]),
  /** The period's live value; null when nothing was measured (missing is never zero). */
  value: z.number().nullable(),
  /** The three weeks before the period, then the period: oldest first (nulls where unmeasured). */
  trend: z.array(z.number().nullable()),
  /** `helper_metric.kpi_weight`; 0 = context (the default). */
  weight: z.number(),
});

export const HelperKpis = z
  .object({
    de_membership_id: Uuid,
    period_key: WeekKey,
    helpers: z.array(
      z.object({
        id: Uuid,
        slug: z.string(),
        name: z.string(),
        status: z.string(),
        relation: z.enum(["lead", "uses"]),
        metrics: z.array(HelperKpiValue),
        /** Tagged spend (`cos-ag:<slug>`) booked to the DE this period, reporting currency. */
        cost_minor: z.number().nullable(),
        /** That spend over the period's `hp.delivered`; null without deliveries. */
        cost_per_delivered_minor: z.number().nullable(),
      }),
    ),
    /** The DE seat's board metrics (`de.pipeline_yield`, `de.first_time_approval_board`). */
    de_metrics: z.array(HelperKpiValue),
    currency: z.string().nullable(),
  })
  .meta({ id: "HelperKpis" });
export type HelperKpis = z.infer<typeof HelperKpis>;

/**
 * RECALL-USAGE (roadmap Phase 1 item 5): `GET kpis/memory-use?days=` — the memory use share,
 * company-wide and per DE, by day, from Recall's rollups (`/api/install/.../memory-usage`): of the
 * records memory delivered in a DE's runs, the share the same run then read, cited or used. Counts
 * only. `enabled` false (and nothing else) while `recall_usage_feed` is off or memory is not
 * connected; `use_share` null with no run-tagged delivery.
 */
export const MemoryUseQuery = z
  .object({ days: z.coerce.number().int().min(1).max(90).default(28) })
  .meta({ id: "MemoryUseQuery" });
const MemoryUseCounts = {
  records_delivered: z.int().min(0),
  records_used: z.int().min(0),
  use_share: z.number().min(0).max(1).nullable(),
};
export const MemoryUseDay = z
  .object({ day: IsoDate, ...MemoryUseCounts })
  .meta({ id: "MemoryUseDay" });
export const MemoryUseShare = z
  .object({
    enabled: z.boolean(),
    /** Why there are no numbers (off, not connected, Recall did not answer), else null. */
    detail: z.string().nullable(),
    since: Timestamp.nullable(),
    until: Timestamp.nullable(),
    company: z.object({ ...MemoryUseCounts, by_day: z.array(MemoryUseDay) }),
    agents: z.array(
      z.object({
        membership_id: Uuid,
        display_name: z.string(),
        ...MemoryUseCounts,
        by_day: z.array(MemoryUseDay),
      }),
    ),
  })
  .meta({ id: "MemoryUseShare" });
export type MemoryUseShare = z.infer<typeof MemoryUseShare>;

/**
 * MEMORY-FIT (design 2026-11-03 §5): `GET kpis/memory-gate?membership_id=` — the eight release-gate
 * metrics of memory, for each of the last two weeks, and whether the release gate is green (all
 * eight green both weeks). Counts only. `enabled` false while the company's `memory_gate_metrics`
 * flag is off. A metric with nothing to count is `unmeasured`, never green.
 */
export const MemoryGateQuery = z
  .object({ membership_id: Uuid.optional() })
  .meta({ id: "MemoryGateQuery" });
export const MemoryGateMetric = z
  .object({
    key: z.enum([
      "use_share",
      "briefs_per_run",
      "brief_tokens",
      "precedents_cited",
      "repeat_questions",
      "duplicate_rate",
      "writes_per_run",
      "eval",
    ]),
    /** The metric's value (a share 0..1, a ratio, tokens, points); null when unmeasured. */
    value: z.number().nullable(),
    /** Its second number where the gate has two (p95 tokens, rulings stored, restatements, mean writes, cost difference). */
    value2: z.number().nullable(),
    numerator: z.number().nullable(),
    denominator: z.number().nullable(),
    status: z.enum(["green", "red", "unmeasured"]),
    /** What it counts and its gate, in words. */
    detail: z.string(),
  })
  .meta({ id: "MemoryGateMetric" });
export type MemoryGateMetric = z.infer<typeof MemoryGateMetric>;
export const MemoryGateWeek = z
  .object({
    since: Timestamp,
    until: Timestamp,
    metrics: z.array(MemoryGateMetric),
    all_green: z.boolean(),
  })
  .meta({ id: "MemoryGateWeek" });
export type MemoryGateWeek = z.infer<typeof MemoryGateWeek>;
export const MemoryGate = z
  .object({
    enabled: z.boolean(),
    detail: z.string().nullable(),
    /** The DE the DE metrics are scoped to; null: the whole company. */
    membership_id: Uuid.nullable(),
    /** The eval's lift threshold in points (the company's setting; default 15). */
    eval_lift_points: z.number(),
    release_green: z.boolean(),
    /** The latest week first. */
    weeks: z.array(MemoryGateWeek),
  })
  .meta({ id: "MemoryGate" });
export type MemoryGate = z.infer<typeof MemoryGate>;

export const agentRecordRoutes = {
  "kpis.memoryGate": defineRoute({
    ...core,
    id: "kpis.memoryGate",
    method: "get",
    path: `${base}/kpis/memory-gate`,
    tag: "digital-employees",
    summary:
      "Memory's eight release-gate metrics for each of the last two weeks, company-wide or for one DE, and whether the gate is green (counts only)",
    access: "viewer",
    params: CompanyParams,
    query: MemoryGateQuery,
    response: { status: 200, schema: MemoryGate },
    errors: ["forbidden"],
    emits: [],
  }),
  "kpis.memoryUse": defineRoute({
    ...core,
    id: "kpis.memoryUse",
    method: "get",
    path: `${base}/kpis/memory-use`,
    tag: "digital-employees",
    summary:
      "The memory use share by day, company-wide and per DE: of the records memory delivered in a run, the share the run then read, cited or used (counts only)",
    access: "viewer",
    params: CompanyParams,
    query: MemoryUseQuery,
    response: { status: 200, schema: MemoryUseShare },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.helperKpis": defineRoute({
    ...core,
    id: "digitalEmployees.helperKpis",
    method: "get",
    path: `${base}/digital-employees/{id}/helpers/kpis`,
    tag: "digital-employees",
    summary:
      "The weekly KPIs of the DE's helpers (AG), derived from the board events the DE attributed to them, plus the DE's pipeline yield and first-time approval",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: HelperKpisQuery,
    response: { status: 200, schema: HelperKpis },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.record": defineRoute({
    ...core,
    id: "digitalEmployees.record",
    method: "get",
    path: `${base}/digital-employees/{id}/record`,
    tag: "digital-employees",
    summary:
      "The DE's track record by week: approved as-is, edited and rejected, outcomes, cost and supervisor time per outcome, interventions, incidents; no ROI basis before four weeks with outcomes",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: DeRecordQuery,
    response: { status: 200, schema: DeRecord },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.recordEvidence": defineRoute({
    ...core,
    id: "digitalEmployees.recordEvidence",
    method: "get",
    path: `${base}/digital-employees/{id}/record/evidence`,
    tag: "digital-employees",
    summary: "What one week of the DE's record counts, newest first (one signal, or all)",
    access: "seat_viewer",
    params: CompanyItemParams,
    query: DeRecordEvidenceQuery,
    response: {
      status: 200,
      schema: z
        .object({ data: z.array(RecordEvidenceItem), next_cursor: z.string().nullable() })
        .meta({ id: "RecordEvidencePage" }),
    },
    errors: ["invalid_cursor"],
    emits: [],
  }),
} as const;
